/**
 * TenantDisputesPanel
 *
 * Renders a card listing this tenant's Helcim chargebacks/disputes. Fetches
 * lazily on mount; renders nothing while loading or when the tenant has no
 * disputes (to avoid noise on the AccountDetail page for the common case).
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@respectly/ui';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type { PaymentDispute } from '@respectly/shared-types';

interface TenantDisputesPanelProps {
  tenantId: string;
  className?: string;
}

const FALLBACK_TONE = { label: 'Unknown', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' };

const STATUS_TONE: Record<string, { label: string; className: string }> = {
  opened: { label: 'Opened', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  under_review: { label: 'Under review', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  needs_response: { label: 'Needs response', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  won: { label: 'Won', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  lost: { label: 'Lost', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
  accepted: { label: 'Accepted', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
  withdrawn: { label: 'Withdrawn', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
};

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(amount);

export function TenantDisputesPanel({ tenantId, className }: TenantDisputesPanelProps) {
  const [disputes, setDisputes] = useState<PaymentDispute[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = getApiClient();
        const data = await api.get<PaymentDispute[]>(
          `${API_ENDPOINTS.SUPER_ADMIN.BILLING.DISPUTES}?tenantId=${encodeURIComponent(tenantId)}`,
        );
        if (!cancelled) setDisputes(data);
      } catch (err) {
        // Failure is non-fatal — surface nothing rather than block the page on a
        // disputes API error. The Network tab still shows the failure.
        console.warn('[TenantDisputesPanel] Failed to load disputes:', err);
        if (!cancelled) setDisputes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (!disputes || disputes.length === 0) return null;

  const openCount = disputes.filter(
    (d) => d.status === 'opened' || d.status === 'needs_response' || d.status === 'under_review',
  ).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={className}
    >
      <Card className="border-red-200/60 dark:border-red-900/40 bg-red-50/30 dark:bg-red-900/10">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-red-900 dark:text-red-200">
            <AlertTriangle className="h-4 w-4" />
            Disputes
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-200 px-1.5 text-xs font-medium text-red-800 dark:bg-red-900/50 dark:text-red-200">
              {disputes.length}
            </span>
            {openCount > 0 && (
              <span className="ml-1 text-xs font-normal text-red-700 dark:text-red-300">
                · {openCount} open
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="divide-y divide-red-200/60 dark:divide-red-900/40">
            {disputes.slice(0, 5).map((dispute) => {
              const tone = STATUS_TONE[dispute.status] ?? FALLBACK_TONE;
              const dateStr = (dispute.openedAt ?? dispute.createdAt)
                ? new Date(dispute.openedAt ?? dispute.createdAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '—';
              return (
                <li key={dispute.id} className="flex items-center justify-between gap-4 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                          tone.className,
                        )}
                      >
                        {tone.label}
                      </span>
                      <p className="truncate text-sm text-red-900 dark:text-red-100">
                        {dispute.reason || 'No reason provided'}
                      </p>
                    </div>
                    <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-300/80">
                      Opened {dateStr}
                      {dispute.helcimDisputeId && (
                        <span className="ml-2 font-mono text-red-700/60 dark:text-red-300/60">
                          {dispute.helcimDisputeId}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold tabular-nums text-red-900 dark:text-red-100">
                      {formatCurrency(dispute.amount, dispute.currency)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
          {disputes.length > 5 && (
            <a
              href="/billing"
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"
            >
              View all {disputes.length} disputes
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
